import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import React, { useEffect, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { getDecimals, getVaultContract } from "../../../../data/smart"
import { ethers } from "ethers"
import LoadingButton from "@mui/lab/LoadingButton"
import { handleJoinTournament } from "../../../../api/web3/handleJoinTournament"
import { ErrorDialog } from "../../../../components/dialog/ErrorDialog"
import { useDialog } from "../../../../context/DialogContext"
import GameDialog from "./GameDialog"

import { TournamentResultDialog } from "./TournamentResultDialog"

const PaymentOption = ({
  active,
  title,
  description,
  balanceLabel,
  balance = null,
  onClick,
  disabled = false,
}) => {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "grid",
        gridTemplateColumns: "44px 1fr",
        gap: 2,
        p: {
          xs: 2,
          sm: 2.5,
        },
        border: active
          ? "1px solid rgba(214, 154, 73, 0.95)"
          : "1px solid rgba(255, 255, 255, 0.14)",
        borderRadius: 2,
        background: active ? "rgba(214,154,73,0.02)" : "rgba(0, 0, 0, 0.28)",
        cursor: "pointer",
        transition: "border-color 0.2s ease, background 0.2s ease",
        "&:hover": {
          borderColor: "rgba(214, 154, 73, 0.75)",
          background: "rgba(214,154,73,0.06)",
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          mt: 0.6,
          borderRadius: "50%",
          border: active
            ? "2px solid #f0bd68"
            : "2px solid rgba(255,255,255,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {active && (
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "#f0bd68",
            }}
          />
        )}
      </Box>

      <Box>
        <Typography
          sx={{
            color: "#c49654",
            fontSize: 24,
            fontWeight: 900,
            lineHeight: 1.05,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 1.2,
            color: "#dedede",
            fontSize: 16,
            lineHeight: 1.45,
            maxWidth: 390,
          }}
        >
          {description}
        </Typography>

        {balance !== null && (
          <Box
            sx={{
              mt: 2,
              pt: 1.6,
              borderTop: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Typography
              sx={{
                color: "#666",
                fontSize: 16,
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {balanceLabel}
            </Typography>

            <Typography
              sx={{
                color: "#c7954f",
                fontSize: 28,
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              {balance} USDT
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const JoinConfirmDialog = ({
  closeDialog,
  tournamentId,
  entryFee,
  user,
  maxWidth = 550,
}) => {
  const [paymentSource, setPaymentSource] = useState("balance")
  const [referralBalance, setReferralBalance] = useState(0)
  const [loading, setLoading] = useState(false)
  const [openErrorDialog, closeErrorDialog] = useDialog()
  const [openGameDialog, closeGameDialog] = useDialog()
  const [openTournamentResultDialog, closeTournamentResultDialog] = useDialog()

  useEffect(() => {
    loadReferralStats()
  }, [user])

  const openTournamentGameDialog = (result = {}) => {
    openGameDialog({
      disableBackdropClick: true,
      disableEscapeKeyDown: true,
      children: (
        <GameDialog
          tournamentId={tournamentId}
          user={user}
          closeDialog={closeGameDialog}
          onGameFinished={openResultDialog}
        />
      ),
    })
  }

  const openResultDialog = (result = {}) => {
    openTournamentResultDialog({
      children: (
        <TournamentResultDialog
          closeDialog={closeTournamentResultDialog}
          playAgain={openGameDialog}
          zombiesKilled={result.zombiesKilled}
          waveReached={result.waveReached}
          bossesKilled={result.bossesKilled}
          score={result.score}
          timePlayed={result.timePlayed}
          position={result.position}
          totalPlayers={result.totalPlayers}
          bestScore={result.bestScore}
          variant={result.variant || "complete"}
        />
      ),
    })
  }

  const loadReferralStats = async () => {
    try {
      const vaultContract = await getVaultContract()
      const myRef = await vaultContract.referrals(user.address)
      const decimals = await getDecimals()

      setReferralBalance(
        ethers.utils.formatUnits(myRef.availableToWithdraw, decimals),
      )
    } catch (error) {
      console.error("Error loading referral stats:", error)
    }
  }

  const onJoinButtonClick = async () => {
    if (loading) return

    setLoading(true)
    try {
      const message = await handleJoinTournament(
        user,
        tournamentId,
        entryFee,
        paymentSource,
      )
      closeDialog()
      setTimeout(() => {
        openTournamentGameDialog()
      }, 0)
    } catch (e) {
      openErrorDialog({
        children: (
          <ErrorDialog
            message={e?.message || String(e)}
            closeDialog={closeErrorDialog}
          />
        ),
      })
    } finally {
      setLoading(false)
    }
  }
  return (
    <Box
      sx={{
        maxWidth,
        textAlign: "center",
        border: "1px solid rgba(198, 146, 76, 0.25)",
        borderRadius: 1,
      }}
    >
      <DialogTitle sx={{ fontSize: "1rem", position: "relative", pr: 6 }}>
        <Typography sx={{ textTransform: "uppercase" }} variant="h1">
          Choose payment source
        </Typography>

        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "#c7954f",
            "&:hover": {
              background: "rgba(214,154,73,0.08)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 0, fontSize: "16px !important" }}>
        <Typography
          sx={{
            color: "#dedede",
            fontSize: 18,
            lineHeight: 1.5,
          }}
        >
          You are about to join the tournament.
          <br />
          Select where you want to pay the entry fee from.
        </Typography>

        {/* Entry Fee value */}
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: 400,
            },
            mx: "auto",
            mt: 2.8,
            py: 1.8,
            px: 2,
            border: "1px solid rgba(255,255,255,0.13)",
            borderRadius: "6px",
            background: "rgba(0,0,0,0.32)",
          }}
        >
          <Typography
            sx={{
              color: "#c7954f",
              fontSize: 16,
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            Entry Fee
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              color: "#c7954f",
              fontSize: 40,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            {entryFee} USDT
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            mt: 2,
            textAlign: "left",
          }}
        >
          {/* From balance option */}
          <PaymentOption
            active={paymentSource === "balance"}
            title="From Balance"
            description="Pay the entry fee using your USDT balance on the platform."
            balanceLabel="Available Balance"
            balance={referralBalance}
            onClick={() => setPaymentSource("balance")}
            disabled={loading}
          />

          {/* From wallet option */}
          <PaymentOption
            active={paymentSource === "wallet"}
            title="From Wallet"
            description="Pay the entry fee directly using your connected wallet."
            onClick={() => setPaymentSource("wallet")}
            disabled={loading}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
            p: {
              xs: 1.7,
              sm: 2,
            },
            display: "grid",
            gridTemplateColumns: "34px 1fr",
            gap: 1.6,
            alignItems: "center",
            textAlign: "left",
            border: "1px solid rgba(214,154,73,0.16)",
            borderRadius: "6px",
            background: "rgba(0,0,0,0.28)",
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: "#d69a49",
              fontSize: 28,
            }}
          />

          <Typography
            sx={{
              color: "#c9975f",
              fontSize: {
                xs: 13,
                sm: 15,
              },
              lineHeight: 1.55,
              fontWeight: 700,
            }}
          >
            Once the entry fee is deducted, you will be automatically registered
            for the tournament. The fee is non-refundable.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, width: "100%", justifyContent: "center" }}>
        <LoadingButton
          variant="contained"
          size="large"
          onClick={onJoinButtonClick}
          loading={loading}
          sx={{ fontSize: 18, minWidth: 250 }}
        >
          Confirm & Join
        </LoadingButton>
      </DialogActions>
    </Box>
  )
}
export default JoinConfirmDialog
